import {
 Link
} from 'react-router-dom'

export function SlackLandingPage(): JSX.Element {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl shadow-soft-lg p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-[#4A154B] rounded-2xl flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-2"><img src="/images/logo/symbol.svg" alt="" className="h-8 w-auto" /><img src="/images/logo/wordmark.svg" alt="Catch" className="h-7 w-auto" /> for Slack</h1>
            <p className="text-gray-600 text-lg">Your AI calendar assistant, right in Slack</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none">
          {/* Overview Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Catch?</h2>
            <p className="text-gray-700 mb-4">
              Catch is your intelligent assistant that writes emails for you, manages your calendar,
              and helps you stay on top of your time. Whether you need to schedule meetings, respond
              to messages, or organize your day, Catch handles it seamlessly.
            </p>
            <p className="text-gray-700">
              Through Slack, you can message Catch directly to get things done. Think of it as your
              personal assistant that takes administrative tasks off your plate, working behind the
              scenes to keep you organized so you can focus on what matters most.
            </p>
          </section>

          {/* How It Works Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How It Works in Slack</h2>
            <p className="text-gray-700 mb-4">
              Once connected, you can chat with Catch straight from Slack's top bar or through direct messages. Here's what you can do:
            </p>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Example Conversations</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">You:</p>
                  <p className="text-gray-800">"Schedule a meeting with Sarah tomorrow at 2pm"</p>
                  <p className="text-sm text-gray-500 mt-3 mb-1">Catch:</p>
                  <p className="text-gray-800">"I've scheduled a meeting with Sarah for tomorrow at 2:00 PM. I've sent her a calendar invite."</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">You:</p>
                  <p className="text-gray-800">"What's on my calendar this week?"</p>
                  <p className="text-sm text-gray-500 mt-3 mb-1">Catch:</p>
                  <p className="text-gray-800">"Here's your schedule for this week: Monday 9am - Team standup, Tuesday 2pm - Product review..."</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">You:</p>
                  <p className="text-gray-800">"Find a 30-minute slot to meet with John next week"</p>
                  <p className="text-sm text-gray-500 mt-3 mb-1">Catch:</p>
                  <p className="text-gray-800">"I found several available times that work for both of you. Would Tuesday at 10am or Thursday at 3pm work better?"</p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Schedule Meetings</strong> - Create calendar events using natural language</li>
              <li><strong>Check Availability</strong> - See your schedule and find free time slots</li>
              <li><strong>Smart Scheduling</strong> - Find mutually available times with colleagues</li>
              <li><strong>Meeting Reminders</strong> - Get notified about upcoming meetings</li>
              <li><strong>Reschedule & Cancel</strong> - Modify your calendar events conversationally</li>
              <li><strong>Daily Briefings</strong> - Get a summary of your day's schedule</li>
            </ul>
          </section>

          {/* Slack Interactions Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How Catch Interacts with Slack</h2>
            <p className="text-gray-700 mb-4">
              Catch uses the following Slack capabilities to help manage your calendar:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Top Bar Access</strong> - Click the Catch icon in Slack's top bar (next to the search bar) to chat in a side panel from anywhere in your workspace</li>
              <li><strong>Direct Messages</strong> - Send messages to Catch to manage your calendar</li>
              <li><strong>Message Responses</strong> - Catch responds with calendar information, confirmations, and suggestions</li>
              <li><strong>Proactive Notifications</strong> - Receive reminders about upcoming meetings and schedule changes</li>
            </ul>
            <p className="text-gray-700">
              Catch only responds to direct messages from you and will never post in public channels
              unless explicitly requested. Your calendar data is only used to provide the scheduling service.
            </p>
          </section>

          {/* Installation Instructions */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Install</h2>
            <p className="text-gray-700 mb-4">
              To connect Catch with your Slack workspace, follow these steps:
            </p>
            <ol className="list-decimal list-inside text-gray-700 mb-6 space-y-3">
              <li>
                <strong>Create a Catch account</strong> - Visit our{' '}
                <Link to="/login" className="text-primary-600 hover:text-primary-700 underline">
                  login page
                </Link>{' '}
                and sign in with your Google account
              </li>
              <li>
                <strong>Connect Google Calendar</strong> - Authorize Catch to access your Google Calendar
                (this happens automatically during sign-up)
              </li>
              <li>
                <strong>Go to Integrations</strong> - Once logged in, navigate to the{' '}
                <Link to="/integrations" className="text-primary-600 hover:text-primary-700 underline">
                  Integrations page
                </Link>
              </li>
              <li>
                <strong>Click "Connect Slack"</strong> - Find the Slack integration card and click the connect button
              </li>
              <li>
                <strong>Authorize in Slack</strong> - You'll be redirected to Slack to authorize Catch for your workspace
              </li>
              <li>
                <strong>Start chatting!</strong> - Once connected, click Catch in Slack's top bar or open a direct message and say hello
              </li>
            </ol>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-blue-800">
                <strong>Note:</strong> You need to sign in first before you can connect Slack. This ensures
                your Google Calendar is connected and your Slack messages are linked to your calendar.
              </p>
            </div>
          </section>

          {/* Configuration Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Configuration</h2>
            <p className="text-gray-700 mb-4">
              After installation, you can customize your Catch experience:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Assistant Character</strong> - Choose your preferred assistant persona from the Integrations page</li>
              <li><strong>Working Hours</strong> - Catch respects your Google Calendar working hours for scheduling suggestions</li>
            </ul>
            <p className="text-gray-700">
              To disconnect Catch from Slack, visit the{' '}
              <Link to="/integrations" className="text-primary-600 hover:text-primary-700 underline">
                Integrations page
              </Link>{' '}
              and click "Disconnect" on the Slack integration card.
            </p>
          </section>

          {/* Data & Privacy Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data & Privacy</h2>
            <p className="text-gray-700 mb-4">
              We take your privacy seriously. Here's how Catch handles your data:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li><strong>Calendar Data</strong> - We access your Google Calendar to read and create events as you request</li>
              <li><strong>Slack Messages</strong> - We process your direct messages to Catch to understand your requests</li>
              <li><strong>No Public Channel Access</strong> - We don't read or post in public channels without your explicit request</li>
              <li><strong>Secure Storage</strong> - All credentials are encrypted using industry-standard encryption</li>
              <li><strong>Data Retention</strong> - OAuth tokens are retained while your connection is active; you can disconnect at any time</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To request access to, transfer, or deletion of your data, please contact us at{' '}
              <a href="mailto:privacy@catchagent.ai" className="text-primary-600 hover:text-primary-700 underline">
                privacy@catchagent.ai
              </a>
            </p>
            <p className="text-gray-700">
              For complete details, please read our{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </Link>.
            </p>
          </section>

          {/* AI Disclaimer Section */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">AI & Accuracy Disclaimer</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-900">
                Catch uses generative AI and Large Language Models (LLMs) to generate drafts, summaries, and suggestions.
                AI-generated content may be inaccurate, incomplete, or out of date and should always be reviewed carefully
                before you share or act on it. Catch is not a source of legal, financial, or medical advice, and you should
                avoid sending highly sensitive personal data in prompts.
              </p>
            </div>
          </section>

          {/* Support Section */}
          <section className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Support</h2>
            <p className="text-gray-700 mb-4">
              Need help? We're here for you:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>
                <strong>Support Page</strong> -{' '}
                <Link to="/support" className="text-primary-600 hover:text-primary-700 underline">
                  Visit our support page
                </Link>
              </li>
              <li>
                <strong>Email</strong> -{' '}
                <a href="mailto:support@catchagent.ai" className="text-primary-600 hover:text-primary-700 underline">
                  support@catchagent.ai
                </a>
              </li>
              <li>
                <strong>Pricing</strong> -{' '}
                <Link to="/pricing" className="text-primary-600 hover:text-primary-700 underline">
                  View our pricing plans
                </Link>
              </li>
            </ul>
          </section>

          {/* CTA Section */}
          <section className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-xl p-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to get started?</h2>
            <p className="text-gray-700 mb-4">
              Sign in to connect Catch with your Slack workspace.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Sign in to Get Started
            </Link>
          </section>
        </div>
      </div>
    </div>
  )
}
